import { useState, useEffect } from 'react';

interface Cup {
  id: number;
  x: number;
  y: number;
  delay: number;
  label: string;
}

const cups: Cup[] = [
  { id: 1, x: 48, y: 18, delay: 0,    label: 'Nuque' },
  { id: 2, x: 32, y: 30, delay: 0.4,  label: 'Épaule G' },
  { id: 3, x: 64, y: 30, delay: 0.8,  label: 'Épaule D' },
  { id: 4, x: 38, y: 44, delay: 1.2,  label: 'Dos sup. G' },
  { id: 5, x: 58, y: 44, delay: 1.6,  label: 'Dos sup. D' },
  { id: 6, x: 35, y: 58, delay: 2.0,  label: 'Milieu dos G' },
  { id: 7, x: 61, y: 58, delay: 2.4,  label: 'Milieu dos D' },
  { id: 8, x: 48, y: 68, delay: 2.8,  label: 'Bas du dos' },
  { id: 9, x: 38, y: 78, delay: 3.2,  label: 'Lombaire G' },
  { id: 10, x: 58, y: 78, delay: 3.6, label: 'Lombaire D' },
];

const phases = [
  { label: 'Préparation', icon: 'ri-drop-line', desc: 'Application d\'huile naturelle sur la peau' },
  { label: 'Pose des ventouses', icon: 'ri-focus-3-line', desc: 'Placement sur les points thérapeutiques' },
  { label: 'Succion active', icon: 'ri-pulse-line', desc: 'Stimulation de la circulation sanguine' },
  { label: 'Libération', icon: 'ri-heart-pulse-line', desc: 'Détoxification et soulagement des tensions' },
];

export default function CuppingAnimation() {
  const [activePhase, setActivePhase] = useState(0);
  const [visibleCups, setVisibleCups] = useState<number[]>([]);
  const [pulsing, setPulsing] = useState(false);
  const [hoveredCup, setHoveredCup] = useState<number | null>(null);
  const [isRunning, setIsRunning] = useState(false);
  const [energyLines, setEnergyLines] = useState(false);

  const startAnimation = () => {
    setVisibleCups([]);
    setPulsing(false);
    setEnergyLines(false);
    setActivePhase(0);
    setIsRunning(true);
  };

  useEffect(() => {
    if (!isRunning) return;

    // Phase 0 → 1 after 1s
    const t0 = setTimeout(() => setActivePhase(1), 800);

    // Reveal cups one by one
    cups.forEach((cup) => {
      const t = setTimeout(() => {
        setVisibleCups((prev) => [...prev, cup.id]);
      }, 1200 + cup.delay * 1000);
      return t;
    });

    // Phase 2 after all cups placed
    const t2 = setTimeout(() => {
      setActivePhase(2);
      setPulsing(true);
    }, 1200 + 3.6 * 1000 + 600);

    // Energy lines
    const t3 = setTimeout(() => {
      setEnergyLines(true);
    }, 1200 + 3.6 * 1000 + 1200);

    // Phase 3
    const t4 = setTimeout(() => {
      setActivePhase(3);
    }, 1200 + 3.6 * 1000 + 3000);

    // Reset
    const t5 = setTimeout(() => {
      setIsRunning(false);
      setActivePhase(0);
      setVisibleCups([]);
      setPulsing(false);
      setEnergyLines(false);
    }, 1200 + 3.6 * 1000 + 6000);

    return () => {
      clearTimeout(t0);
      clearTimeout(t2);
      clearTimeout(t3);
      clearTimeout(t4);
      clearTimeout(t5);
    };
  }, [isRunning]);

  return (
    <div className="relative w-full rounded-3xl overflow-hidden bg-gradient-to-br from-stone-900 via-stone-800 to-stone-900" style={{ minHeight: '500px' }}>
      {/* Background glow */}
      <div className="absolute inset-0 opacity-20">
        <div className="absolute top-1/4 left-1/4 w-64 h-64 rounded-full bg-emerald-500 blur-3xl"></div>
        <div className="absolute bottom-1/4 right-1/4 w-48 h-48 rounded-full bg-amber-500 blur-3xl"></div>
      </div>

      {/* Title bar */}
      <div className="relative z-10 px-6 pt-6 pb-3 flex items-center justify-between">
        <div>
          <div className="text-emerald-400 text-xs font-semibold tracking-widest uppercase mb-1">Simulation interactive</div>
          <div className="text-white font-bold text-lg" style={{ fontFamily: "'Playfair Display', serif" }}>
            Ventouses en action
          </div>
        </div>
        <button
          onClick={startAnimation}
          disabled={isRunning}
          className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-500 disabled:bg-stone-600 disabled:cursor-not-allowed text-white text-xs font-semibold px-4 py-2 rounded-full transition-all duration-300 cursor-pointer whitespace-nowrap"
        >
          <i className={`${isRunning ? 'ri-loader-4-line animate-spin' : 'ri-play-fill'}`}></i>
          {isRunning ? 'En cours...' : 'Lancer la démo'}
        </button>
      </div>

      {/* Main animation area */}
      <div className="relative z-10 flex items-center justify-center px-6 pb-4" style={{ minHeight: '320px' }}>
        {/* Body silhouette */}
        <div className="relative" style={{ width: '220px', height: '300px' }}>
          {/* Body shape using CSS */}
          <div className="absolute inset-0 flex items-center justify-center">
            {/* Torso */}
            <div
              className="absolute rounded-3xl"
              style={{
                width: '110px',
                height: '220px',
                top: '40px',
                left: '50%',
                transform: 'translateX(-50%)',
                background: 'linear-gradient(180deg, #c4a882 0%, #b8956e 50%, #a07850 100%)',
                opacity: 0.85,
              }}
            ></div>
            {/* Head */}
            <div
              className="absolute rounded-full"
              style={{
                width: '60px',
                height: '70px',
                top: '-10px',
                left: '50%',
                transform: 'translateX(-50%)',
                background: 'linear-gradient(180deg, #c4a882 0%, #b8956e 100%)',
                opacity: 0.85,
              }}
            ></div>
            {/* Left arm */}
            <div
              className="absolute rounded-2xl"
              style={{
                width: '28px',
                height: '160px',
                top: '55px',
                left: '18px',
                background: 'linear-gradient(180deg, #c4a882 0%, #b8956e 100%)',
                opacity: 0.75,
                transform: 'rotate(8deg)',
              }}
            ></div>
            {/* Right arm */}
            <div
              className="absolute rounded-2xl"
              style={{
                width: '28px',
                height: '160px',
                top: '55px',
                right: '18px',
                background: 'linear-gradient(180deg, #c4a882 0%, #b8956e 100%)',
                opacity: 0.75,
                transform: 'rotate(-8deg)',
              }}
            ></div>
            {/* Spine line */}
            <div
              className="absolute rounded-full opacity-30"
              style={{
                width: '3px',
                height: '180px',
                top: '50px',
                left: '50%',
                transform: 'translateX(-50%)',
                background: 'linear-gradient(180deg, transparent, #78350f, transparent)',
              }}
            ></div>
          </div>

          {/* Energy lines */}
          {energyLines && cups.map((cup) => (
            <div
              key={`energy-${cup.id}`}
              className="absolute rounded-full"
              style={{
                left: `${cup.x}%`,
                top: `${cup.y}%`,
                width: '60px',
                height: '60px',
                transform: 'translate(-50%, -50%)',
                border: '1px solid rgba(52, 211, 153, 0.3)',
                animation: `energyRing 2s ease-out infinite`,
                animationDelay: `${cup.delay * 0.3}s`,
              }}
            ></div>
          ))}

          {/* Cups */}
          {cups.map((cup) => {
            const isVisible = visibleCups.includes(cup.id);
            const isHovered = hoveredCup === cup.id;
            return (
              <div
                key={cup.id}
                className="absolute cursor-pointer"
                style={{
                  left: `${cup.x}%`,
                  top: `${cup.y}%`,
                  transform: 'translate(-50%, -50%)',
                  opacity: isVisible ? 1 : 0,
                  transition: 'opacity 0.4s ease, transform 0.4s ease',
                  zIndex: 20,
                }}
                onMouseEnter={() => setHoveredCup(cup.id)}
                onMouseLeave={() => setHoveredCup(null)}
              >
                {/* Cup shape */}
                <div
                  style={{
                    width: '28px',
                    height: '28px',
                    borderRadius: '50% 50% 50% 50% / 60% 60% 40% 40%',
                    background: 'radial-gradient(circle at 40% 35%, rgba(255,255,255,0.6) 0%, rgba(180,230,210,0.4) 40%, rgba(52,211,153,0.15) 100%)',
                    border: '2px solid rgba(52, 211, 153, 0.8)',
                    boxShadow: pulsing
                      ? '0 0 12px rgba(52,211,153,0.6), inset 0 2px 4px rgba(255,255,255,0.3)'
                      : '0 0 6px rgba(52,211,153,0.3), inset 0 2px 4px rgba(255,255,255,0.2)',
                    animation: pulsing ? `cupPulse 1.8s ease-in-out infinite` : 'none',
                    animationDelay: `${cup.delay * 0.2}s`,
                    backdropFilter: 'blur(4px)',
                    transition: 'box-shadow 0.5s ease',
                  }}
                ></div>

                {/* Suction indicator */}
                {pulsing && (
                  <div
                    className="absolute inset-0 rounded-full"
                    style={{
                      animation: `suctionRing 1.8s ease-out infinite`,
                      animationDelay: `${cup.delay * 0.2}s`,
                      border: '1px solid rgba(52,211,153,0.5)',
                      borderRadius: '50%',
                    }}
                  ></div>
                )}

                {/* Tooltip */}
                {isHovered && (
                  <div
                    className="absolute bg-stone-900 text-white text-xs px-2 py-1 rounded-lg whitespace-nowrap pointer-events-none"
                    style={{
                      bottom: '110%',
                      left: '50%',
                      transform: 'translateX(-50%)',
                      border: '1px solid rgba(52,211,153,0.4)',
                      zIndex: 30,
                    }}
                  >
                    <span className="text-emerald-400">{cup.label}</span>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Side info */}
        <div className="absolute right-6 top-1/2 -translate-y-1/2 flex flex-col gap-2">
          {visibleCups.length > 0 && (
            <div className="bg-stone-800/80 rounded-xl p-3 border border-emerald-500/20 text-center" style={{ minWidth: '80px' }}>
              <div className="text-emerald-400 font-bold text-xl">{visibleCups.length}</div>
              <div className="text-stone-400 text-xs">ventouses</div>
              <div className="text-stone-500 text-xs">posées</div>
            </div>
          )}
          {pulsing && (
            <div className="bg-stone-800/80 rounded-xl p-3 border border-amber-500/20 text-center">
              <div className="w-6 h-6 flex items-center justify-center mx-auto mb-1">
                <i className="ri-heart-pulse-line text-amber-400 text-lg"></i>
              </div>
              <div className="text-amber-400 text-xs font-semibold">Actif</div>
            </div>
          )}
        </div>
      </div>

      {/* Phase indicators */}
      <div className="relative z-10 px-6 pb-6">
        <div className="grid grid-cols-4 gap-2">
          {phases.map((phase, idx) => (
            <div
              key={phase.label}
              className="rounded-xl p-2 text-center transition-all duration-500"
              style={{
                background: activePhase === idx
                  ? 'rgba(52, 211, 153, 0.15)'
                  : activePhase > idx
                  ? 'rgba(52, 211, 153, 0.06)'
                  : 'rgba(255,255,255,0.03)',
                border: activePhase === idx
                  ? '1px solid rgba(52,211,153,0.5)'
                  : activePhase > idx
                  ? '1px solid rgba(52,211,153,0.2)'
                  : '1px solid rgba(255,255,255,0.06)',
              }}
            >
              <div className={`w-6 h-6 flex items-center justify-center mx-auto mb-1 rounded-full transition-all duration-500 ${
                activePhase === idx ? 'bg-emerald-500/30' : activePhase > idx ? 'bg-emerald-500/15' : 'bg-stone-700/50'
              }`}>
                <i className={`${phase.icon} text-xs ${
                  activePhase === idx ? 'text-emerald-400' : activePhase > idx ? 'text-emerald-600' : 'text-stone-500'
                }`}></i>
              </div>
              <div className={`text-xs font-semibold transition-colors duration-500 ${
                activePhase === idx ? 'text-emerald-400' : activePhase > idx ? 'text-stone-400' : 'text-stone-600'
              }`}>
                {phase.label}
              </div>
              <div className="text-stone-600 text-xs leading-tight mt-0.5 hidden lg:block">{phase.desc}</div>
            </div>
          ))}
        </div>
      </div>

      {/* CSS Animations */}
      <style>{`
        @keyframes cupPulse {
          0%, 100% { transform: scale(1); box-shadow: 0 0 8px rgba(52,211,153,0.5); }
          50% { transform: scale(1.12); box-shadow: 0 0 18px rgba(52,211,153,0.8); }
        }
        @keyframes suctionRing {
          0% { transform: scale(1); opacity: 0.8; }
          100% { transform: scale(2.5); opacity: 0; }
        }
        @keyframes energyRing {
          0% { transform: translate(-50%, -50%) scale(0.5); opacity: 0.6; }
          100% { transform: translate(-50%, -50%) scale(3); opacity: 0; }
        }
      `}</style>
    </div>
  );
}
